import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Progress,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DocumentTrainingCard from '../components/DocumentTrainingCard';
import { groupTrainingDocuments } from '../utils/trainingDocuments';
import EmployeeInstructions from '../components/EmployeeInstructions';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Test, TestAttempt, TrainingFolder, TrainingVideo } from '../types';
import {
  TEST_DIFFICULTY_COLOR,
  TEST_DIFFICULTY_LABEL,
} from '../constants/testDifficulty';
import { formatTime } from '../utils/formatTime';
import './Dashboard.css';

const { Title, Text, Paragraph } = Typography;

interface DashboardStats {
  total_attempts: number;
  passed_attempts: number;
  failed_attempts: number;
  average_score: number;
  average_time_seconds: number;
}

type DashboardVideo = TrainingVideo & { folderTitle: string };

const numericScore = (value?: number | null) => Number(value ?? 0);

const plural = (count: number, one: string, few: string, many: string) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
};

const dueMeta = (iso?: string | null) => {
  if (!iso) return { label: 'Без срока', tone: 'default' as const };
  const due = dayjs(iso);
  const hours = due.diff(dayjs(), 'hour', true);
  if (hours <= 24) {
    return { label: `До ${due.format('DD.MM, HH:mm')}`, tone: 'error' as const };
  }
  if (hours <= 72) {
    return { label: `До ${due.format('DD.MM, HH:mm')}`, tone: 'warning' as const };
  }
  return { label: `До ${due.format('DD.MM.YYYY')}`, tone: 'default' as const };
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignedTests, setAssignedTests] = useState<Test[]>([]);
  const [attempts, setAttempts] = useState<TestAttempt[]>([]);
  const [folders, setFolders] = useState<TrainingFolder[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');


  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const responses = await Promise.allSettled([
      api.get<Test[]>('/tests/?assigned_only=true'),
      api.get<TestAttempt[]>('/results/my-attempts'),
      api.get<DashboardStats>('/results/my-statistics'),
      api.get<TrainingFolder[]>('/video/folders?include_videos=true'),
    ]);

    const [testsResponse, attemptsResponse, statsResponse, foldersResponse] = responses;
    if (testsResponse.status === 'fulfilled') setAssignedTests(testsResponse.value.data ?? []);
    if (attemptsResponse.status === 'fulfilled') setAttempts(attemptsResponse.value.data ?? []);
    if (statsResponse.status === 'fulfilled') setStats(statsResponse.value.data);
    if (foldersResponse.status === 'fulfilled') setFolders(foldersResponse.value.data ?? []);

    const failedCount = responses.filter((response) => response.status === 'rejected').length;
    if (failedCount) {
      setLoadError(
        failedCount === responses.length
          ? 'Не удалось загрузить данные личного кабинета'
          : 'Часть данных временно недоступна',
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const sortedTests = useMemo(() => [...assignedTests].sort((left, right) => {
    const leftStarted = String(left.assignment_status || '').toLowerCase() === 'started';
    const rightStarted = String(right.assignment_status || '').toLowerCase() === 'started';
    if (leftStarted !== rightStarted) return leftStarted ? -1 : 1;
    const leftDue = left.assignment_due_at ? dayjs(left.assignment_due_at).valueOf() : Number.MAX_SAFE_INTEGER;
    const rightDue = right.assignment_due_at ? dayjs(right.assignment_due_at).valueOf() : Number.MAX_SAFE_INTEGER;
    return leftDue - rightDue;
  }), [assignedTests]);

  const allVideos = useMemo<DashboardVideo[]>(() => folders
    .flatMap((folder) => (folder.videos || []).map((video) => ({
      ...video,
      folderTitle: folder.title,
    })))
    .sort((left, right) => {
      const leftCompleted = Boolean(left.progress?.is_completed);
      const rightCompleted = Boolean(right.progress?.is_completed);
      if (leftCompleted !== rightCompleted) return leftCompleted ? 1 : -1;
      const leftProgress = numericScore(left.progress?.percent_watched);
      const rightProgress = numericScore(right.progress?.percent_watched);
      if (Boolean(leftProgress) !== Boolean(rightProgress)) return leftProgress ? -1 : 1;
      return rightProgress - leftProgress;
    }), [folders]);

  const pendingVideos = useMemo(
    () => allVideos.filter((video) => !video.progress?.is_completed),
    [allVideos],
  );

  const sortedAttempts = useMemo(() => [...attempts]
    .sort((left, right) => dayjs(right.completed_at || right.started_at).valueOf()
      - dayjs(left.completed_at || left.started_at).valueOf()), [attempts]);

  const visibleVideos = pendingVideos.slice(0, 4);
  const visibleAttempts = sortedAttempts.slice(0, 4);

  const pendingDocumentTrainings = useMemo(() => folders.flatMap(folder => (
    groupTrainingDocuments(folder.documents || []).map(training => ({ ...training, folderTitle: folder.title }))
  )).filter(training => !training.isCompleted)
    .sort((left, right) => (right.trainingDate || '').localeCompare(left.trainingDate || '')), [folders]);
  const pendingDocuments = pendingDocumentTrainings.reduce((sum, training) => sum + training.documents.filter(document => !document.progress?.is_completed).length, 0);

  const totalVideos = folders.reduce((sum, folder) => sum + folder.video_count, 0);
  const completedVideos = folders.reduce((sum, folder) => sum + folder.completed_videos, 0);
  const totalDocuments = folders.reduce((sum, folder) => sum + (folder.document_count || 0), 0);
  const completedDocuments = folders.reduce((sum, folder) => sum + (folder.completed_documents || 0), 0);
  const totalMaterials = totalVideos + totalDocuments;
  const completedMaterials = completedVideos + completedDocuments;
  const firstName = user?.full_name?.trim().split(/\s+/)[0]
    || 'сотрудник';
  const workCompleted = sortedTests.length === 0 && pendingVideos.length === 0 && pendingDocuments === 0;

  return (
    <Layout>
      <main className="employee-dashboard" data-guide="dashboard-home">
        <header className="employee-dashboard__hero">
          <div>
            <Text className="employee-dashboard__eyebrow">ЛИЧНЫЙ КАБИНЕТ</Text>
            <Title level={1}>Здравствуйте, {firstName}</Title>
            <Paragraph>
              Здесь собраны ваши обязательные материалы, назначенные тесты и результаты обучения.
            </Paragraph>
          </div>
          <div className={`employee-dashboard__plan${workCompleted ? ' is-complete' : ''}`}>
            <span className="employee-dashboard__plan-icon">
              {workCompleted ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            </span>
            <div>
              <strong>{workCompleted ? 'Всё выполнено' : 'План обучения'}</strong>
              <span>
                {workCompleted
                  ? 'Новых обязательных материалов нет'
                  : `${sortedTests.length} ${plural(sortedTests.length, 'тест', 'теста', 'тестов')} · ${pendingVideos.length} видео · ${pendingDocuments} документов`}
              </span>
            </div>
          </div>
        </header>

        <EmployeeInstructions />

        {loadError && (
          <Alert
            type="warning"
            showIcon
            message={loadError}
            action={<Button size="small" icon={<ReloadOutlined />} onClick={loadDashboard}>Повторить</Button>}
          />
        )}

        {loading ? (
          <div className="employee-dashboard__loading">
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        ) : (
          <>
            <section data-guide="dashboard-summary" className="employee-dashboard__stats" aria-label="Сводка обучения">
              <article className="employee-stat-card blue">
                <span><FileTextOutlined /></span>
                <div><strong>{sortedTests.length}</strong><small>назначено тестов</small></div>
              </article>
              <article className="employee-stat-card purple">
                <span><VideoCameraOutlined /></span>
                <div><strong>{pendingVideos.length + pendingDocuments}</strong><small>материалов к изучению</small></div>
              </article>
              <article className="employee-stat-card green">
                <span><TrophyOutlined /></span>
                <div><strong>{stats?.passed_attempts ?? 0}</strong><small>сдано тестов</small></div>
              </article>
              <article className="employee-stat-card amber">
                <span><BarChartOutlined /></span>
                <div><strong>{numericScore(stats?.average_score).toFixed(1)}%</strong><small>средний результат</small></div>
              </article>
            </section>

            <div className="employee-dashboard__columns">
              <section data-guide="dashboard-tests" id="assigned-tests" className="employee-panel employee-panel--tests">
                <div className="employee-panel__head">
                  <div>
                    <Text className="employee-panel__kicker">НУЖНО ВЫПОЛНИТЬ</Text>
                    <Title level={3}>Назначенные тесты</Title>
                  </div>
                </div>
                {sortedTests.length ? (
                  <div className="employee-task-list">
                    {sortedTests.map((test) => {
                      const status = String(test.assignment_status || '').toLowerCase();
                      const due = dueMeta(test.assignment_due_at);
                      return (
                        <article className="employee-test-item" key={test.id}>
                          <div className="employee-test-item__icon"><FileTextOutlined /></div>
                          <div className="employee-test-item__body">
                            <div className="employee-test-item__tags">
                              {test.subgroup && <Tag color="blue">{test.subgroup}</Tag>}
                              {test.difficulty && (
                                <Tag color={TEST_DIFFICULTY_COLOR[test.difficulty]}>
                                  {TEST_DIFFICULTY_LABEL[test.difficulty]}
                                </Tag>
                              )}
                              <Tag color={due.tone} icon={<CalendarOutlined />}>{due.label}</Tag>
                            </div>
                            <Title level={4}>{test.title}</Title>
                            <Space size={14} wrap className="employee-test-item__meta">
                              <Text>{test.questions_count ?? 0} вопросов</Text>
                              <Text>{test.time_limit_minutes} мин</Text>
                            </Space>
                          </div>
                          <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => navigate(`/tests/${test.id}`)}
                          >
                            {status === 'started' ? 'Продолжить' : 'Начать'}
                          </Button>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Назначенных тестов сейчас нет" />
                )}
              </section>

              <section data-guide="dashboard-materials" className="employee-panel employee-panel--videos">
                <div className="employee-panel__head">
                  <div>
                    <Text className="employee-panel__kicker">МАТЕРИАЛЫ ОБУЧЕНИЯ</Text>
                    <Title level={3}>Видео и документы</Title>
                  </div>
                  <Button type="link" onClick={() => navigate('/videos')}>
                    Все материалы ({totalMaterials}) <ArrowRightOutlined />
                  </Button>
                </div>
                {visibleVideos.length ? (
                  <div className="employee-video-list">
                    {visibleVideos.map((video) => {
                      const percent = Math.round(numericScore(video.progress?.percent_watched));
                      return (
                        <button
                          type="button"
                          className="employee-video-item"
                          key={video.id}
                          onClick={() => navigate(`/videos/${video.id}`)}
                        >
                          <span className="employee-video-item__play"><PlayCircleOutlined /></span>
                          <span className="employee-video-item__body">
                            <small>{video.folderTitle}</small>
                            <strong>{video.title}</strong>
                            <span className="employee-video-item__progress">
                              <Progress percent={percent} size="small" showInfo={false} />
                              <em>{percent ? `${percent}%` : 'Не начато'}</em>
                            </span>
                          </span>
                          <ArrowRightOutlined className="employee-video-item__arrow" />
                        </button>
                      );
                    })}
                  </div>
                ) : pendingDocumentTrainings.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Все опубликованные материалы изучены" />
                ) : null}
                {pendingDocumentTrainings.length > 0 && <Space direction="vertical" className="video-list" size={12}>
                  {pendingDocumentTrainings.slice(0, 4).map(training => <DocumentTrainingCard key={training.key} training={training} folderTitle={training.folderTitle} />)}
                </Space>}
                {totalMaterials > 0 && (
                  <div className="employee-panel__footer">
                    Изучено {completedMaterials} из {totalMaterials}
                    <Progress
                      percent={Math.round((completedMaterials / totalMaterials) * 100)}
                      size="small"
                      showInfo={false}
                    />
                  </div>
                )}
              </section>
            </div>

            <section data-guide="dashboard-results" className="employee-panel employee-panel--results">
              <div className="employee-panel__head">
                <div>
                  <Text className="employee-panel__kicker">ДИНАМИКА ОБУЧЕНИЯ</Text>
                  <Title level={3}>Последние результаты</Title>
                </div>
                <Button type="link" onClick={() => navigate('/results')}>
                  Вся история <ArrowRightOutlined />
                </Button>
              </div>
              {visibleAttempts.length ? (
                <div className="employee-results-grid">
                  {visibleAttempts.map((attempt) => (
                    <button
                      type="button"
                      className="employee-result-card"
                      key={attempt.id}
                      onClick={() => navigate('/results')}
                    >
                      <span className={`employee-result-card__score${attempt.is_passed ? ' is-passed' : ' is-failed'}`}>
                        {numericScore(attempt.score).toFixed(0)}%
                      </span>
                      <span className="employee-result-card__body">
                        <strong>{attempt.test_title || 'Тест'}</strong>
                        <small>
                          {dayjs(attempt.completed_at || attempt.started_at).format('DD.MM.YYYY, HH:mm')}
                          {attempt.time_spent_seconds
                            ? ` · ${formatTime(attempt.time_spent_seconds)}`
                            : ''}
                        </small>
                      </span>
                      <Tag color={attempt.is_passed ? 'success' : 'error'}>
                        {attempt.is_passed ? 'Сдан' : 'Не сдан'}
                      </Tag>
                    </button>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Завершённых тестов пока нет" />
              )}
            </section>
          </>
        )}

      </main>
    </Layout>
  );
};

export default Dashboard;
